import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { ActionIconDef, GridActionIconClickEvent, GridConfig, GridHyperlinkClickEvent, GridRow } from '@inavista/components/grid';
import { APP_CONFIG, AppConfig } from 'projects/shared/src/lib/app-config.token';
import { UserRoles } from 'projects/sales/src/app/shared/constants/app.constants';
import { CommonService } from 'projects/sales/src/app/shared/services/common.service';
import { AdvancedSearchEmployeesConfigurations } from '../../../model/user-profile.model';

// Employee id Advanced Search (Sales and Service sections), "Select ISC Manager" and "View ISC Employees", on
// @inavista/components. Rows: RIMS Master api/grid/user-profile-search/employees?mode= (paged in SQL; before, four old
// endpoints that loaded every row: ServiceEmployee/GetAllEmployeeDetails, odata/ServiceEmployee/Get, GetISCManagers,
// GetISCEmployees). Same columns and rules: a picked employee (the arrow, the Employee Id or the Name) goes back to the
// section with isInteracted, an ISC manager to assigneISCManagerDetailsToISC; the ISC employees of a manager are only viewed
// (no selection, no Service Department). Excel for every role but Sales Rep.
const MODES: Record<AdvancedSearchEmployeesConfigurations['fetchIdsFor'], string> = {
  'sales-employeeIds': 'sales',
  'service-employeeIds': 'service',
  'service-isc-employees': 'isc',
  'service-isc-managers': 'isc-managers',
};
const COLUMNS = ['userId', 'employeeId', 'employeeName', 'serviceDepartment', 'serviceManager', 'email', 'telephone', 'addressCode',
  'address', 'houseNumber', 'city', 'state', 'country'];
const SELECT_ICON = '<line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />';

@Component({
    selector: 'app-advanced-employee-search-popup',
    templateUrl: './advanced-employee-search-popup.component.html',
    styleUrls: ['./advanced-employee-search-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdvancedEmployeeSearchPopupComponent implements OnInit {
  @Input('configs') configs: AdvancedSearchEmployeesConfigurations;
  @Output() onEmployeeSelected = new EventEmitter();
  @Output() assigneISCManagerDetailsToISC = new EventEmitter();
  @Output() onClose = new EventEmitter();
  isExpanded = false;
  gridConfig: GridConfig;
  actionIcons: ActionIconDef[] = [];
  private viewOnly = false;

  constructor(@Inject(APP_CONFIG) private config: AppConfig, private commonService: CommonService) {}

  ngOnInit() {
    this.viewOnly = !!this.configs?.filterParentId || this.configs?.popupTitle === 'View ISC Employees';
    const mode = MODES[this.configs?.fetchIdsFor] ?? 'service';
    const columns = COLUMNS.filter(c => !(this.configs?.filterParentId && c === 'serviceDepartment'));
    this.actionIcons = this.viewOnly ? [] : [{ key: 'select', label: 'Select Employee', icon: SELECT_ICON, colorFn: () => '#000000de' }];
    this.gridConfig = {
      apiBaseUrl: `${this.config.masterApiURL}grid/user-profile-search/employees`,
      queryParams: { mode, ...(mode === 'isc' ? { parentEmployeeId: String(this.configs?.filterParentId ?? '') } : {}) },
      keyColumn: 'id',
      clientSide: false,
      pageSize: 200,
      rowHeight: 26,
      loadingIndicator: 'banner',
      stretchColumns: true,
      wrapHeaders: true,
      enableSearch: true,
      enableContextMenuFilter: true,
      enableFitColumns: true,
      enableResetLayout: true,
      enableExport: this.commonService.getUSER()?.role !== UserRoles.salesRep,
      defaultColumnOrder: columns,
      // The old hidden columns (still in the column picker).
      startHiddenColumns: ['userId', 'serviceManager', 'addressCode', 'address', 'houseNumber', 'country'],
      // Every other column of the row (the profile data the section copies from it): never shown.
      hiddenByDefaultColumns: ['id', 'telephone2', 'firstDateOfEmployment', 'lastDateOfEmployment', 'serviceCar', 'warehouse',
        'orderCurrency', 'employeeType', 'serviceDepartmentName', 'name', 'name2', 'address2', 'postBoxNumber', 'zipCode',
        'servicetelephone', 'serviceEmail', 'countryId', 'parentEmployeeId', 'iscSoldToBP', 'payVia', 'iscCode', 'iscSupCode',
        't1LaborRate', 'mileageRate', 't2LaborRate', 'mileageThreshold', 't1CalloutRate', 't2CalloutRate', 'role', 'employeeIdDesc',
        ...(this.configs?.filterParentId ? ['serviceDepartment'] : [])],
      columnLabels: {
        userId: 'User Id', employeeId: 'Employee Id', employeeName: 'Name', serviceDepartment: 'Service Department',
        serviceManager: 'Service Manager', email: 'Email', telephone: 'Mobile', addressCode: 'Address Code', address: 'Address',
        houseNumber: 'House Number', city: 'City', state: 'State', country: 'Country',
      },
      columnWidths: Object.fromEntries(columns.map(c => [c, 120])),
      columnAlign: Object.fromEntries(columns.map(c => [c, {
        header: { horizontal: 'left' as const },
        ...(['userId', 'employeeId', 'addressCode'].includes(c) ? { horizontal: 'center' as const } : {}),
      }])),
      excelFilterColumns: ['serviceDepartment', 'serviceManager', 'city', 'state', 'country'],
      hyperlinkColumns: this.viewOnly ? [] : ['employeeId', 'employeeName'],
      emptyStateMessage: mode === 'isc' ? 'This ISC manager has no ISC employees.' : 'No employees to show.',
      noResultsMessage: 'No employee matches. Clear the search or the filters to see them all.',
      persistenceKey: 'rims.users.employee-search',
      persistenceVersion: 1,
    };
  }

  private pick(row: GridRow) {
    if (this.viewOnly) return;
    if (this.configs?.fetchIdsFor === 'service-isc-managers') this.assigneISCManagerDetailsToISC.emit({ ...row, isInteracted: true });
    else this.onEmployeeSelected.emit({ ...row, isInteracted: true });
    this.close();
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    if (event.iconKey === 'select') this.pick(event.row);
  }

  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    this.pick(event.row);
  }

  expandCollapse() {
    this.isExpanded = !this.isExpanded;
  }

  close() {
    this.onClose.emit(true);
  }
}
